// ============================================================
// useStickyBar.ts — (Settlement redesign) keeps the settlement's action bar at the
// foot of the visible area, so the main button is in one place whatever is above
// it. position: sticky does the pinning, but on a short step the bar would simply
// follow the content, so the page is stretched to the bottom of the visible area
// (empty page behind the cards, nothing inside them). On a phone the section starts
// below the first screen, so the bar is fixed to the screen instead, and the page
// keeps room for it at its end. Also:
//   • focus never lands behind the bar (scroll-padding-bottom = the bar's height);
//   • with a phone's keyboard up the bar steps aside, so it can't cover the field.
// The scroller is <main> from tablet width up, the page itself on a phone.
// ============================================================
import { useEffect, useState } from 'react'

const KEYBOARD_PX = 150
export const keyboardUp = (fullHeight: number, visibleHeight: number): boolean => fullHeight - visibleHeight > KEYBOARD_PX

export const useStickyBar = (page: HTMLElement | null, bar: HTMLElement | null): { typing: boolean } => {
  const [typing, setTyping] = useState(false)
  useEffect(() => {
    if (!page) return undefined
    const main = page.closest('main')
    const scroller: HTMLElement = main && getComputedStyle(main).overflowY !== 'visible' ? main : document.documentElement
    const inside = scroller !== document.documentElement
    const measure = (): void => {
      const top = page.getBoundingClientRect().top + scroller.scrollTop - (inside ? scroller.getBoundingClientRect().top : 0)
      page.style.minHeight = inside ? `${Math.max(0, scroller.clientHeight - top)}px` : ''
      page.style.paddingBottom = !inside && bar ? `${bar.offsetHeight + 12}px` : ''
      if (bar) scroller.style.scrollPaddingBottom = `${bar.offsetHeight + 12}px`
    }
    measure()
    window.addEventListener('resize', measure)
    const seen = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    seen?.observe(scroller)
    if (bar) seen?.observe(bar)
    return () => {
      window.removeEventListener('resize', measure)
      seen?.disconnect()
      scroller.style.scrollPaddingBottom = ''
      page.style.minHeight = ''
      page.style.paddingBottom = ''
    }
  }, [page, bar])
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return undefined
    let full = window.innerHeight
    const check = (): void => {
      full = Math.max(full, window.innerHeight)
      setTyping(window.innerWidth < 768 && keyboardUp(full, vv.height))
    }
    const turned = (): void => { full = window.innerHeight }
    vv.addEventListener('resize', check)
    window.addEventListener('orientationchange', turned)
    return () => { vv.removeEventListener('resize', check); window.removeEventListener('orientationchange', turned) }
  }, [])
  return { typing }
}
